// src/pages/financials/AgingSection.tsx
//
// Shared A/R + A/P aging view (recipes: qbd-ar-aging / qbd-ap-aging).
// Bucket tiles (Current / 1-30 / 31-60 / 61-90 / 90+ / Total) from the
// response totals, then a per-customer/vendor table sorted by total desc
// (server order preserved), sticky name column, CSV export. Zero buckets
// render as a muted dash. `asOf` (yyyy-MM-dd string) is shown next to the
// title, rendered as-is.

import { useQuery } from '@tanstack/react-query';
import { RefreshCw, Download, AlertTriangle, FileSpreadsheet } from 'lucide-react';
import { apiV2 as api } from '../../services/apiV2';
import type { QbdAgingResponse, QbdAgingRow } from '../../types';
import { exportCsv, type CsvColumns } from '../../lib/csvExport';
import { exportXlsx } from '../../lib/xlsxExport';

const fmt0 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtCur = (v: number) => fmt0.format(v);

const BUCKETS: { key: keyof Omit<QbdAgingRow, 'name'>; label: string }[] = [
  { key: 'current', label: 'Current' },
  { key: 'd1_30',   label: '1–30' },
  { key: 'd31_60',  label: '31–60' },
  { key: 'd61_90',  label: '61–90' },
  { key: 'd90Plus', label: '90+' },
];

function BucketCell({ v }: { v: number }) {
  if (v === 0) return <span className="text-slate-600">–</span>;
  return <span className={v < 0 ? 'text-red-400' : undefined}>{fmtCur(v)}</span>;
}

interface AgingSectionProps {
  kind: 'ar' | 'ap';
  title: string;
  description: string;
  nameLabel: string;   // "Customer" | "Vendor"
  icon: React.ComponentType<{ size?: number }>;
}

export function AgingSection({ kind, title, description, nameLabel, icon: Icon }: AgingSectionProps) {
  const q = useQuery({
    queryKey: ['qbd', `${kind}-aging`],
    queryFn: () => (kind === 'ar' ? api.getQbdArAging() : api.getQbdApAging()),
    staleTime: 60_000,
    retry: 1,
  });

  const data: QbdAgingResponse | undefined = q.data;
  const rows = data?.rows ?? [];

  const exportAging = (exporter: typeof exportCsv = exportCsv) => {
    const cols: CsvColumns<QbdAgingRow> = [
      [nameLabel, (r) => r.name],
      ...BUCKETS.map((b): [string, (r: QbdAgingRow) => number] => [b.label, (r) => r[b.key]]),
      ['Total', (r) => r.total],
    ];
    exporter(`${kind}-aging`, cols, rows);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Icon size={20} /> {title}
            {data?.asOf && <span className="text-xs font-normal text-slate-500 ml-1">as of {data.asOf}</span>}
          </h2>
          <p className="text-sm text-slate-400 mt-0.5">{description}</p>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => exportAging()}
            disabled={rows.length === 0}
            title="Export the aging table to CSV"
            className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
            <Download size={11} /> Export
          </button>
          <button
            onClick={() => exportAging(exportXlsx)}
            disabled={rows.length === 0}
            title="Export to Excel"
            className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
            <FileSpreadsheet size={11} /> Excel
          </button>
          <button onClick={() => q.refetch()} disabled={q.isLoading}
            className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
            <RefreshCw size={14} className={q.isFetching ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {q.error && (
        <div className="p-4 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm flex items-center gap-2">
          <AlertTriangle size={16} />
          <div>
            <div className="font-medium">Couldn't load {title}</div>
            <div className="text-xs text-red-300/70 mt-0.5">{(q.error as Error).message}</div>
          </div>
        </div>
      )}

      {q.isLoading ? (
        <div className="flex items-center justify-center h-40"><RefreshCw className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : !q.error && data && (
        <>
          {/* Bucket tiles */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {BUCKETS.map((b) => (
              <div key={b.key} className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
                <p className="text-xs text-slate-500">{b.label}</p>
                <p className="text-lg font-bold text-white tabular-nums">{fmtCur(data.totals[b.key])}</p>
              </div>
            ))}
            <div className="p-3 rounded-lg bg-indigo-500/10 border border-indigo-500/30">
              <p className="text-xs text-indigo-300/70">Total</p>
              <p className="text-lg font-bold text-white tabular-nums">{fmtCur(data.totals.total)}</p>
            </div>
          </div>

          {/* Table */}
          <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-700/50 bg-slate-800/50">
                    {/* Sticky identifying column — opaque bg so scrolled content doesn't show through */}
                    <th className="sticky left-0 z-10 bg-slate-800 px-3 py-2.5 text-left text-slate-400 font-medium min-w-[180px]">{nameLabel}</th>
                    {BUCKETS.map((b) => (
                      <th key={b.key} className="px-3 py-2.5 text-right text-slate-400 font-medium whitespace-nowrap">{b.label}</th>
                    ))}
                    <th className="px-3 py-2.5 text-right text-slate-300 font-semibold whitespace-nowrap">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.name} className="group border-b border-slate-800/50 hover:bg-slate-700/20 transition-colors">
                      <td className="sticky left-0 z-10 bg-slate-900 group-hover:bg-slate-800 px-3 py-2 text-white max-w-[240px] truncate" title={r.name}>{r.name}</td>
                      {BUCKETS.map((b) => (
                        <td key={b.key} className="px-3 py-2 text-right text-slate-300 tabular-nums whitespace-nowrap"><BucketCell v={r[b.key]} /></td>
                      ))}
                      <td className="px-3 py-2 text-right font-medium text-white tabular-nums whitespace-nowrap">{fmtCur(r.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {rows.length === 0 && (
              <div className="text-center py-8 text-slate-500 text-sm">Nothing outstanding — all clear</div>
            )}
            <div className="px-3 py-2 border-t border-slate-700/30 text-xs text-slate-500">
              {rows.length} {nameLabel.toLowerCase()}{rows.length === 1 ? '' : 's'} · as of {data.asOf}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
